import React, { forwardRef } from 'react';
import { StyleSheet, View } from 'react-native';
import Svg, { Path, G } from 'react-native-svg';
import { LayerData } from '../core/svgParser';
import { Animated } from 'react-native';

const FilteredPath = forwardRef((props: any, ref) => {
  const {
    collapsable,
    onStartShouldSetResponder,
    onResponderTerminationRequest,
    onResponderGrant,
    onResponderMove,
    onResponderRelease,
    onResponderTerminate,
    ...rest
  } = props;
  return <Path ref={ref as any} {...rest} />;
});
FilteredPath.displayName = 'FilteredPath';

const AnimatedPath = Animated.createAnimatedComponent(FilteredPath);

interface SvgCanvasProps {
  layers: LayerData[];
  capturedLayerIds: string[];
  activeLayerId: string | null;
  onLayerPress: (id: string) => void;
  isTargetVisible?: boolean;
  isCompletion?: boolean;
  svgRef?: React.RefObject<any>; // Use 'any' to accommodate both web SVG and React Native Svg
  opacityAnims?: Map<string, Animated.Value>;
  width?: number;
  height?: number;
  preserveAspectRatio?: string;
}

const BaseLayer = React.memo(function BaseLayer({ layer, isActive, isCaptured, onLayerPress, isTargetVisible, isCompletion, animatedOpacity }: any) {
  const isVisualHidden = (isActive || isCaptured) && !isTargetVisible && !isCompletion;

  const fillStyle = (isTargetVisible || isCompletion) ? layer.targetHex : (isVisualHidden ? layer.targetHex : "#fafafa");
  const fillOpacityVal = isVisualHidden ? 0.01 : 1;

  const strokeStyle = (isTargetVisible || isCompletion) ? layer.targetHex : (isVisualHidden ? "none" : "#666");
  const strokeWidthVal = (isTargetVisible || isCompletion) ? 1.5 : 2;

  // Combine the logic-based opacity with the animation-based opacity
  const finalOpacity = animatedOpacity || 1;

  return (
    <AnimatedPath
      d={layer.d}
      fill={fillStyle}
      fillOpacity={fillOpacityVal}
      stroke={strokeStyle}
      strokeWidth={strokeWidthVal}
      vectorEffect="non-scaling-stroke"
      onPress={() => onLayerPress(layer.id)}
      pointerEvents="all"
      style={{
        opacity: finalOpacity,
        cursor: 'pointer'
      } as any}
      {...({
        className: isCaptured ? 'captured' : 'template-view',
        'data-type': isCaptured ? 'captured' : 'template'
      } as any)}
    />
  );
});

BaseLayer.displayName = 'BaseLayer'; // <-- This satisfies ESLint

export default function SvgCanvas({
  layers,
  capturedLayerIds,
  activeLayerId,
  onLayerPress,
  isTargetVisible,
  isCompletion,
  svgRef,
  opacityAnims,
  width = 800,
  height = 1067,
  preserveAspectRatio = "xMidYMid slice"
}: SvgCanvasProps) {
  const activeLayer = layers.find(l => l.id === activeLayerId);

  return (
    <View style={[StyleSheet.absoluteFill, styles.canvasStyle]} >
      <Svg
        // @ts-ignore - We know this is only used on web where the ref type is compatible
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: '100%', height: '100%' }}
        preserveAspectRatio={preserveAspectRatio}
      >
        <G>
          {layers.map((layer) => (
            <BaseLayer
              key={`base-${layer.id}`}
              layer={layer}
              isActive={layer.id === activeLayerId}
              isCaptured={capturedLayerIds.includes(layer.id)}
              onLayerPress={onLayerPress}
              isTargetVisible={isTargetVisible}
              isCompletion={isCompletion}
              animatedOpacity={opacityAnims?.get(layer.id)}
            />
          ))}

          {/* Active Outline (Only show if not in target-view mode) */}
          {activeLayer && !isTargetVisible && (
            <Path
              d={activeLayer.d}
              fill="transparent"
              stroke={activeLayer.targetHex}
              strokeWidth={4}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          )}
        </G>
      </Svg>
    </View>
  );
}

const styles = StyleSheet.create({
  canvasStyle: {
    userSelect: 'none'
  }
});